import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './BusinessCardPage.module.css';
import Header from '../../components/Header/Header';
import CardIconButton from '../../components/UI/CardIconButton/CardIconButton';
import { useFavorite } from '../FavoritePage/FavoriteContext';
import { useCompare } from '../ComparePage/CompareContext';
import BuyCard from '../../components/BuyCard/BuyCard';
import Button from '../../components/UI/Button/Button';
import Footer from '../../components/Footer/Footer';
import StatusProject from '../../components/StatusProject/StatusProject';
import SimilarCard from '../../components/SimilarCard/SimilarCard';
import PreviewBusiness from '../../components/BusinessPageComponents/PreviewBusiness/PreviewBusiness';
import AdditionalInfo from '../../components/CardPageComponents/CardPageAdditionalInfo/CardPageAdditionalInfo';
import CardPageFeatures from '../../components/CardPageComponents/CardPageFeatures/CardPageFeatures';
import { Helmet } from 'react-helmet';
import cn from 'classnames'
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import ObjectNotFound from '../../components/ObjectNotFound/ObjectNotFound';
import { OBJECT_TYPES } from '../../components/ObjectNotFound/ObjectNotFound';
import { calculateDiscountedPrice, calculateDiscountedPricePerM2, formatPrice } from '../../utils/discountPriceUtils';
import { PURCHASE_BUSINESS_PAGE } from '../../router/paths';
import { useBusiness } from './BusinessContext';
import { api } from '../../utils/api';

export default function BusinessCardPage() {
  const { favorites, toggleFavorite } = useFavorite();
  const { compares, toggleCompare } = useCompare();
  const navigate = useNavigate();
  const { setBusiness: setBusinessContext, selectedOption, setSelectedOption } = useBusiness();

  const [business, setBusiness] = useState<any>(null);
  const [similarBusinesss, setSimilarBusinesss] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 1080);
  const { id } = useParams();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);
  const [isLoading, setIsLoading] = useState(true);
  const [mortgagePeriod, setMortgagePeriod] = useState<number>(48);

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth);
    return () => {
      window.removeEventListener('resize', updateWindowWidth);
    };
  }, []);

  const buttonStyle = {
    width: windowWidth <= 767 ? '100%' : 'fit-content',
  };

  useEffect(() => {
    const updateLayout = () => {
      const windowWidth = window.innerWidth;
      setIsMobile(windowWidth <= 1080);
    };

    updateLayout();

    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  });

  const parseArea = (areaString: string): number => {
    return Number(areaString.replace(',', '.'));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [businesses, selectedBusiness, mortgageResponse] = await Promise.all([
          api.business.getAll(),
          api.business.getById(Number(id)),
          api.mortgage.getAll()
        ]);

        if (mortgageResponse && mortgageResponse.length > 0) {
          setMortgagePeriod(mortgageResponse[0].period);
        }

        if (!selectedBusiness) {
          setError('Помещение не найдено');
        } else {
          console.log('🏢 BusinessCardPage: Raw business data:', selectedBusiness);
          console.log('🏢 BusinessCardPage: specialOptions:', selectedBusiness.specialOptions);
          setBusiness(selectedBusiness);

          const selectedArea = parseArea((selectedBusiness.area ?? '').toString());

          const filteredBusinesses = businesses.filter((business: any) => {
            const businessArea = parseArea((business.area ?? '').toString());
            const areaDifference = Math.abs(businessArea - selectedArea);
            return business.id !== selectedBusiness.id && areaDifference <= 20;
          });

          setSimilarBusinesss(getRandomItems(filteredBusinesses, 3));
        }
      } catch (err) {
        setError('Произошла ошибка при загрузке данных');
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };
    fetchData();
  }, [id, setIsInitialLoad]);
  
  const getRandomItems = (array: any[], count: number) => {
    const shuffled = array.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <ObjectNotFound type={OBJECT_TYPES.BUSINESS}/>;
  }

  if (!business) {
    return <ObjectNotFound type={OBJECT_TYPES.BUSINESS}/>;
  }

  const isFavorite = favorites.some(
    (item) => item.id === business.id && item.type === business.type
  );
  const isCompared = compares.some(
    (item) => item.id === business.id && item.type === business.type
  );

  const handleToggleFavorite = () => {
    if (!business) return;
    toggleFavorite({
      ...business,
      type: 'business',
      roomType: '',
      entrance: 0,
      imageSecond: '',
      projectType: '',
      totalEntrances: 0,
      totalFloors: 0,
      livingArea: '',
    });
  };

  const handleToggleCompare = () => {
    if (!business) return;
    toggleCompare({
      ...business,
      type: 'business',
      roomType: '',
      entrance: 0,
      imageSecond: '',
      projectType: '',
      totalEntrances: 0,
      livingArea: '',
    });
  };

  const parsePrice = (price: any): number => {
    return Number((price || '').toString().replace(/\s/g, '')) || 0;
  };

  const basePrice = parsePrice(business.price ?? '0');
  const discountedPrice = calculateDiscountedPrice(
    business.pricePerM2 ?? '0',
    business.discountProperty ?? 0,
    business.price ?? '0'
  );
  const computedPrice = parsePrice(discountedPrice);

  const isDiscounted = Boolean(business.discountProperty && business.discountProperty > 0);

  const fullPaymentAmount = computedPrice - (business.discountFull || 0);
  
  const installmentPrice = basePrice;
  
  const pricePerM2 = parsePrice(business.pricePerM2 ?? '0');

  const formattedPricePerM2 = formatPrice(business.pricePerM2);
  const discountedPricePerM2 = business.pricePerM2 && business.discountProperty && business.discountProperty > 0
    ? calculateDiscountedPricePerM2(business.pricePerM2, business.discountProperty)
    : formattedPricePerM2;

  const installmentYears = Math.round(mortgagePeriod / 12);

  const downPayment = Math.round(installmentPrice * 0.3);
  const monthlyPayment = Math.round((installmentPrice - downPayment) / mortgagePeriod);
  const numberOfPayments = mortgagePeriod;

  const handleCardSelect = (option: 'full' | 'installment') => {
    setSelectedOption(option);
  };
  
  const displayedPrice = selectedOption === 'full' ? fullPaymentAmount : installmentPrice;

  const discountPercentage = business.discountProperty ?? 0;
  const discountAmount = basePrice - computedPrice;
  const totalDiscountAmount = discountAmount + (business.discountFull || 0);

  const handleShareLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
  };

  const handlePrint = () => {
    const printStyle = document.createElement('style');
    printStyle.innerHTML = `
      @media print {
        @page {
          size: landscape;
        }
      }
    `;
    document.head.appendChild(printStyle);
    window.print();
    document.head.removeChild(printStyle);
  };

  const handleRouteClick = () => {
    window.open('https://yandex.ru/maps/11008/kaspiysk/?ll=47.622042%2C42.898893&mode=routes&rtext=~42.898949%2C47.621987&rtt=auto&ruri=~ymapsbm1%3A%2F%2Forg%3Foid%3D117585465488&z=18.53', '_blank');
  };

  const handleBuyClick = () => {
    if (business) {
      setBusinessContext(business);
      navigate(PURCHASE_BUSINESS_PAGE.replace(':id', business.id.toString()));
    }
  };

  const BuyWrapper = () => {
    return (
      <div className={styles.buy_wrapper}>
        <div className={styles.icon_buttons}>
          <CardIconButton iconType='icon2' isActive={isFavorite} onClick={handleToggleFavorite} />
          <CardIconButton iconType='icon3' isActive={isCompared} onClick={handleToggleCompare} />
          <div className={styles.share_button_wrapper}>
            <CardIconButton iconType='share_icon' />
            <div className={styles.share_options}>
              <CardIconButton iconType='shareLinkIcon' onClick={handleShareLink} />
              <CardIconButton iconType='sharePrintIcon' onClick={handlePrint} />
            </div>
          </div>
        </div>
        <div className={styles.buy_info}>
          <div className={styles.title}>
            <h3>
              Помещение {business.area != null && business.area !== '' && !isNaN(Number((business.area ?? '').toString().replace(/\s/g, '')))
                ? Number((business.area ?? '').toString().replace(/\s/g, '')).toLocaleString('ru-RU')
                : '—'} м²
            </h3>
            <h3>на {business.floor === 0 ? 'цокольном' : business.floor} этаже</h3>
          </div>
          <div className={styles.price}>
            <h3>
              {displayedPrice.toLocaleString('ru-RU')} ₽
            </h3>
            {isDiscounted && selectedOption === 'full' && (
              <div className={cn('text_body_b2', styles.price_no_discount)}>
                <p>{basePrice.toLocaleString('ru-RU')} ₽</p>
              </div>
            )}
          </div>
          <div className={styles.price_per_m}>
            <span className='text_body_b2'>
              {selectedOption === 'full'
                ? `${discountedPricePerM2} ₽ за м²`
                : `${formattedPricePerM2} ₽ за м²`}
            </span>
          </div>
        </div>
        <div className={styles.buy_cards_wrapper}>
        <BuyCard
          fullPaymentAmount={fullPaymentAmount}
          discountPercentage={discountPercentage}
          discountAmount={totalDiscountAmount}
          installmentYears={installmentYears}
          downPayment={downPayment}
          monthlyPayment={monthlyPayment}
          numberOfPayments={numberOfPayments}
          discountFull={business.discountFull ?? 0}
          installmentPrice={installmentPrice}
          onCardSelect={handleCardSelect}
          selectedOption={selectedOption}
        />
        </div>
        <Button colorType='blue' roundType='small_rounded' onClick={handleBuyClick}>
          Забронировать
        </Button>
        <div className={cn('text_captions_c1', styles.underbutton_span)}>
          <span>
            Стоимость носит информационный характер и&nbsp;не&nbsp;является публичной офертой.
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{`Помещение ${business.area != null && business.area !== '' && !isNaN(Number((business.area ?? '').toString().replace(/\s/g, '')))
          ? Number((business.area ?? '').toString().replace(/\s/g, '')).toLocaleString('ru-RU')
          : '—'} м² | Восток Строй`}</title>
        {/* <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" /> */}
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="noindex, nofollow" />
        {/* <link rel="canonical" href="https://vostokstroy.com/parking" /> */}

        {/* <meta property="og:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." /> */}
        {/* <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" /> */}
        {/* <meta property="og:url" content="https://vostokstroy.com/parking" /> */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        {/* <meta name="twitter:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." /> */}
        {/* <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" /> */}

        <link rel="icon" type="image/svg+xml" href="/favicons/status-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/status-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/status-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/status-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/status-fav-180x180.png" />
      </Helmet>
      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme='light' />
          <div className={styles.content}>
            <div className={styles.info_buy_wrapper}>
              <PreviewBusiness businessData={business}></PreviewBusiness>
              {isMobile && <BuyWrapper />}
              <div className={styles.info_wrapper}>
                {(business.specialOptions && business.specialOptions.length > 0
                  || business.windowOptions && business.windowOptions.length > 0) && (
                  <div className={styles.card_page_features}>
                    <CardPageFeatures data={business} type='business' />
                  </div>
                )}
                <div className={styles.similar_wrapper}>
                  <div className={styles.block_title}>
                    <h3>Похожие</h3>
                  </div>
                  <div className={styles.similar_content}>
                    {similarBusinesss.map((similarBusiness) => (
                      <SimilarCard
                        key={similarBusiness.id}
                        data={similarBusiness}
                        isDiscounted={
                          typeof similarBusiness.discountProperty === 'number'
                            ? similarBusiness.discountProperty > 0
                            : Boolean(similarBusiness.discountProperty)
                        }
                      />
                    ))}
                  </div>
                </div>
                <AdditionalInfo property={business}></AdditionalInfo>
              </div>
            </div>
            {!isMobile && <BuyWrapper />}
          </div>
          <div className={styles.black_block}>
            <div className={styles.contact_info_container}>
              <div className={styles.info_card}>
                <div className={styles.card_content}>
                  <h3 className={styles.address_text}>Наш офис находится на проспекте Омарова, 8</h3>
                </div>
                <Button roundType='small_rounded' onClick={handleRouteClick} style={buttonStyle}>
                  Построить маршрут
                </Button>
              </div>

              <div className={styles.info_card}>
                <div className={styles.card_content}>
                  <div className={styles.contact_details}>
                    <h3 className={styles.phone_text}>+7 938 151-22-22</h3>
                    <p className={cn('text_body_b2', styles.hours_text)}>
                      Мы&nbsp;рады звонкам. Отвечаем с&nbsp;9:00 до&nbsp;19:00 по&nbsp;будням и&nbsp;с&nbsp;9:00 до&nbsp;18:00 по&nbsp;выходным.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <StatusProject />
          </div>
          <div className={styles.footer_wrapper}>
            <Footer theme='dark' />
          </div>
        </>
      )}
    </>
  );
}
